import { z } from 'zod';
import { ExpenseStatus, FuelType } from '../enums.js';

const isoDateTime = z.string().datetime({ offset: true });
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** EGP amounts: positive, at most 2 decimals, capped well below Decimal(14,2). */
export const egp = z.number().positive().max(99_999_999).multipleOf(0.01);

export const FuelInputSchema = z.object({
  liters: z.number().positive().max(9999).multipleOf(0.01),
  /** Optional: derived from amount / liters when omitted. */
  pricePerLiter: z.number().positive().max(9999).multipleOf(0.001).nullable().optional(),
  /** Optional: defaults to the vehicle's fuel type. */
  fuelType: z.enum(FuelType).nullable().optional(),
  odometerKm: z.number().min(0).max(9_999_999).nullable().optional(),
  isFullTank: z.boolean().default(true),
  stationPlaceId: z.string().uuid().nullable().optional(),
});
export type FuelInput = z.infer<typeof FuelInputSchema>;

export const CreateExpenseSchema = z.object({
  categoryId: z.string().uuid(),
  vehicleId: z.string().uuid().nullable().optional(),
  driverId: z.string().uuid().nullable().optional(),
  tripId: z.string().uuid().nullable().optional(),
  amountEgp: egp,
  occurredAt: isoDateTime.optional(),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lon: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().uuid().nullable().optional(),
  locationText: z.string().max(200).nullable().optional(),
  receiptFileId: z.string().uuid().nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
  isRebillable: z.boolean().default(false),
  /** Required when the category is a fuel category. */
  fuel: FuelInputSchema.nullable().optional(),
});
export type CreateExpense = z.infer<typeof CreateExpenseSchema>;

/** Driver-app variant: driverId is resolved from the session; vehicle defaults to the driver's vehicle. */
export const DriverCreateExpenseSchema = CreateExpenseSchema.omit({ driverId: true, isRebillable: true });
export type DriverCreateExpense = z.infer<typeof DriverCreateExpenseSchema>;

export const UpdateExpenseSchema = CreateExpenseSchema.partial();
export type UpdateExpense = z.infer<typeof UpdateExpenseSchema>;

export const RejectExpenseSchema = z.object({ reason: z.string().min(2).max(300) });
export type RejectExpense = z.infer<typeof RejectExpenseSchema>;

export const ExpensesQuerySchema = z.object({
  status: z.enum(ExpenseStatus).optional(),
  categoryId: z.string().uuid().optional(),
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
  tripId: z.string().uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  /** Only rebillable expenses not yet on an invoice. */
  uninvoiced: z.coerce.boolean().optional(),
});
export type ExpensesQuery = z.infer<typeof ExpensesQuerySchema>;

export const ExpenseAnalyticsQuerySchema = z.object({
  from: isoDate.optional(),
  to: isoDate.optional(),
  vehicleId: z.string().uuid().optional(),
  driverId: z.string().uuid().optional(),
});
export type ExpenseAnalyticsQuery = z.infer<typeof ExpenseAnalyticsQuerySchema>;

export const FuelConsumptionQuerySchema = z.object({
  vehicleId: z.string().uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type FuelConsumptionQuery = z.infer<typeof FuelConsumptionQuerySchema>;

export const DriverExpensesScope = ['all', 'pending', 'approved', 'rejected'] as const;
export type DriverExpensesScope = (typeof DriverExpensesScope)[number];
